import { Component, EventEmitter, OnInit, Output, ViewChild, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { NgSelectComponent } from '@ng-select/ng-select';
import { InspectorsService } from 'src/app/core/services/inspectors/inspectors.service';
import { InsightsFilters } from '../../models/insights-dashboard.model';
import { CohortsService } from '../../services/cohorts/cohorts.service';
import { SpecialtiesService } from '../../services/specialties/specialties.service';
import { FilterOption, STATUS_OPTIONS, DATE_RANGE_OPTIONS, DEFAULT_FILTERS } from './insights-filters.data';

@Component({
    selector: 'app-ask-ayen-insights-filters',
    templateUrl: './insights-filters.component.html',
    styleUrl: './insights-filters.component.scss',
    standalone: false
})
export class InsightsFiltersComponent implements OnInit {
  @Output() filtersChange = new EventEmitter<InsightsFilters>();

  private cohortsService = inject(CohortsService);
  private specialtiesService = inject(SpecialtiesService);
  private inspectorsService = inject(InspectorsService);

  filters: InsightsFilters = { ...DEFAULT_FILTERS };

  statusOptions = STATUS_OPTIONS;
  dateRangeOptions = DATE_RANGE_OPTIONS;
  cohortOptions: FilterOption[] = [];
  specialtyOptions: FilterOption[] = [];
  expertOptions: FilterOption[] = [];

  expertsPage = 1;
  expertsPageCount = 1;
  expertsSearchInput = '';
  expertsSearchTimeout: ReturnType<typeof setTimeout> | undefined;
  private expertsRequestSub: Subscription | undefined;

  @ViewChild('selectExperts') selectExperts!: NgSelectComponent;

  ngOnInit(): void {
    this.loadCohorts();
    this.loadSpecialties();
    this.loadExperts();
    // emit the default filters immediately so the parent triggers the initial /dashboard/ load
    this.onChange();
  }

  onChange(): void {
    // custom date_range requires both bounds per the API contract; don't emit a half-set custom range
    if (this.filters.dateRange === 'custom' && (!this.filters.dateFrom || !this.filters.dateTo)) {
      return;
    }
    this.filtersChange.emit({ ...this.filters });
  }

  onDateRangeChange(): void {
    if (this.filters.dateRange !== 'custom') {
      this.filters.dateFrom = null;
      this.filters.dateTo = null;
    }
    this.onChange();
  }

  reset(): void {
    this.filters = { ...DEFAULT_FILTERS };
    this.onChange();
  }

  private loadCohorts(): void {
    this.cohortsService.getCohorts({ page_size: 100 }).subscribe((response: any) => {
      const options = (response?.data || []).map((cohort: { id: number; name: string }) => ({
        value: cohort.id,
        label: cohort.name,
      }));
      this.cohortOptions = options;
    });
  }

  private loadSpecialties(): void {
    this.specialtiesService.getSpecialties({ page_size: 100 }).subscribe((response) => {
      const options = (response?.data || []).map((specialty) => ({
        value: specialty.id,
        label: specialty.name_ar,
      }));
      this.specialtyOptions = options;
    });
  }

  private loadExperts(): void {
    const params: { [key: string]: any } = { page: this.expertsPage, page_size: 20 };
    if (this.expertsSearchInput) {
      params['full_name'] = this.expertsSearchInput;
    }

    // cancel any still in-flight request so a slow, stale response can't land after
    // a newer keystroke and append outdated results onto the current list
    this.expertsRequestSub?.unsubscribe();
    this.expertsRequestSub = this.inspectorsService.getAvailableInspectors(params).subscribe((response: any) => {
      const newOptions = (response?.results || []).map((expert: { id: number; full_name: string }) => ({
        value: expert.id,
        label: expert.full_name,
      }));
      this.expertOptions = [...this.expertOptions, ...newOptions];
      this.expertsPageCount = response?.pages_count || 1;
    });
  }

  onScrollExpertsPagination(): void {
    if (this.expertsPage < this.expertsPageCount) {
      this.expertsPage++;
      this.loadExperts();
    }
  }

  resetExpertsSearch(): void {
    this.expertOptions = [];
    this.expertsPage = 1;
    this.expertsSearchInput = '';
    clearTimeout(this.expertsSearchTimeout);
    this.loadExperts();
  }

  searchExpertsText(): void {
    this.expertsSearchInput = this.selectExperts.searchTerm;
    this.expertOptions = [];
    this.expertsPage = 1;
    this.expertsRequestSub?.unsubscribe();
    clearTimeout(this.expertsSearchTimeout);
    this.expertsSearchTimeout = setTimeout(() => {
      this.loadExperts();
    }, 300);
  }
}
